import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IMAGES } from '../layout/theme';
import MainBanner from '../components/MainBanner';
import { apiClient } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { ERROR_MESSAGES } from '../utils/errorHandler';
import LanguageSwitcher from '../components/LanguageSwitcher';

const Login = () => {
    const navigate = useNavigate();
    const { login } = useAuth();
    const { t } = useTranslation();
    const [showEmailLogin, setShowEmailLogin] = useState(false);
    const [loginError, setLoginError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleGoogleLogin = () => {
        const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
        const redirectUri = import.meta.env.VITE_GOOGLE_REDIRECT_URI;
        const scope = 'openid email profile';

        const googleAuthUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
            `client_id=${clientId}&` +
            `redirect_uri=${encodeURIComponent(redirectUri)}&` +
            `response_type=code&` +
            `scope=${encodeURIComponent(scope)}&` +
            `access_type=offline&` +
            `prompt=select_account`;

        window.location.href = googleAuthUrl;
    };

    const handleMicrosoftLogin = () => {
        const clientId = import.meta.env.VITE_MICROSOFT_CLIENT_ID;
        const redirectUri = import.meta.env.VITE_MICROSOFT_REDIRECT_URI;
        const tenant = import.meta.env.VITE_MICROSOFT_TENANT;
        const scope = 'openid email profile User.Read';

        const microsoftAuthUrl = `https://login.microsoftonline.com/${tenant}/oauth2/v2.0/authorize?` +
            `client_id=${clientId}&` +
            `redirect_uri=${encodeURIComponent(redirectUri)}&` +
            `response_type=code&` +
            `scope=${encodeURIComponent(scope)}&` +
            `prompt=select_account`;

        window.location.href = microsoftAuthUrl;
    };

    const handleEmailLogin = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setLoginError('');
        const formData = new FormData(e.currentTarget);
        const email = formData.get('email') as string;
        const password = formData.get('password') as string;

        setIsLoading(true);
        try {
            await apiClient.post('/login/oauth', { provider: 'email', email, password });
            await login();
            navigate('/cms/reservations');
        } catch (err: any) {
            setLoginError(ERROR_MESSAGES.LOGIN_FAILED);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <section className="main-banner" id="main-login" style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            paddingTop: '120px',
            paddingBottom: '60px',
            position: 'relative',
            overflowX: 'hidden',
            maxWidth: '100vw'
        }}>
            <MainBanner />
            {/* Language Switcher in top right */}
            <div style={{
                position: 'absolute',
                top: '30px',
                right: '30px',
                zIndex: 10
            }}>
                <LanguageSwitcher isSticky={false} />
            </div>
            <Link
                to="/"
                style={{
                    position: 'absolute',
                    top: '30px',
                    left: '30px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 24px',
                    backgroundColor: '#ffffff',
                    color: '#2c3d4f',
                    textDecoration: 'none',
                    borderRadius: '4px',
                    fontSize: '16px',
                    fontWeight: '500',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                    transition: 'all 0.3s',
                    zIndex: 10
                }}
                onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = '#d89d37';
                    e.currentTarget.style.color = '#ffffff';
                }}
                onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = '#ffffff';
                    e.currentTarget.style.color = '#2c3d4f';
                }}
            >
                <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                >
                    <path d="M19 12H5M12 19l-7-7 7-7"/>
                </svg>
                {t('loginPage.backToSite')}
            </Link>
            <div className="container">
                <div className="row justify-content-center">
                    <div className="col-lg-5 col-md-8">
                        <div className="login-box" style={{
                            backgroundColor: '#ffffff',
                            padding: '40px',
                            borderRadius: '8px',
                            boxShadow: '0 2px 20px rgba(0,0,0,0.1)'
                        }}>
                            <div className="text-center mb-4">
                                <img src={IMAGES.logoWithColor} alt="Pure Key Homes" style={{ maxWidth: '200px', marginBottom: '20px' }} />
                                <h3 className="h3-title">{t('loginPage.welcomeBack')}</h3>
                                <p style={{ color: '#666' }}>{t('loginPage.signInToAccount')}</p>
                            </div>

                            {!showEmailLogin ? (
                                <div className="oauth-buttons">
                                    <button
                                        onClick={handleGoogleLogin}
                                        className="oauth-btn"
                                        style={{
                                            width: '100%',
                                            padding: '15px',
                                            marginBottom: '15px',
                                            border: '1px solid #c8d8dd',
                                            backgroundColor: '#ffffff',
                                            borderRadius: '4px',
                                            fontSize: '16px',
                                            fontWeight: '500',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '10px',
                                            color: '#2c3d4f',
                                            transition: 'all 0.3s'
                                        }}
                                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
                                    >
                                        <svg width="20" height="20" viewBox="0 0 24 24">
                                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                                        </svg>
                                        {t('loginPage.continueWithGoogle')}
                                    </button>

                                    <button
                                        onClick={handleMicrosoftLogin}
                                        className="oauth-btn"
                                        style={{
                                            width: '100%',
                                            padding: '15px',
                                            marginBottom: '15px',
                                            border: '1px solid #c8d8dd',
                                            backgroundColor: '#ffffff',
                                            borderRadius: '4px',
                                            fontSize: '16px',
                                            fontWeight: '500',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '10px',
                                            color: '#2c3d4f',
                                            transition: 'all 0.3s'
                                        }}
                                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
                                    >
                                        <svg width="20" height="20" viewBox="0 0 23 23">
                                            <path fill="#f3f3f3" d="M0 0h23v23H0z"/>
                                            <path fill="#f35325" d="M1 1h10v10H1z"/>
                                            <path fill="#81bc06" d="M12 1h10v10H12z"/>
                                            <path fill="#05a6f0" d="M1 12h10v10H1z"/>
                                            <path fill="#ffba08" d="M12 12h10v10H12z"/>
                                        </svg>
                                        {t('loginPage.continueWithMicrosoft')}
                                    </button>

                                    <div style={{
                                        textAlign: 'center',
                                        margin: '25px 0',
                                        position: 'relative'
                                    }}>
                                        <span style={{
                                            backgroundColor: '#ffffff',
                                            padding: '0 15px',
                                            color: '#999',
                                            position: 'relative',
                                            zIndex: 1
                                        }}>{t('loginPage.or')}</span>
                                        <div style={{
                                            position: 'absolute',
                                            top: '50%',
                                            left: 0,
                                            right: 0,
                                            height: '1px',
                                            backgroundColor: '#c8d8dd',
                                            zIndex: 0
                                        }}></div>
                                    </div>

                                    <button
                                        onClick={() => setShowEmailLogin(true)}
                                        className="sec-btn"
                                        style={{
                                            width: '100%',
                                            padding: '15px',
                                            border: 'none',
                                            backgroundColor: '#d89d37',
                                            borderRadius: '4px',
                                            fontSize: '16px',
                                            fontWeight: '500',
                                            cursor: 'pointer',
                                            color: '#ffffff'
                                        }}
                                    >
                                        <span>{t('loginPage.loginWithEmail')}</span>
                                    </button>
                                </div>
                            ) : (
                                <div className="email-login-form">
                                    {loginError && (
                                        <div style={{
                                            backgroundColor: '#fef2f2',
                                            border: '1px solid #fecaca',
                                            color: '#dc2626',
                                            padding: '12px 16px',
                                            borderRadius: '4px',
                                            marginBottom: '20px',
                                            fontSize: '14px'
                                        }}>
                                            {loginError}
                                        </div>
                                    )}
                                    <form onSubmit={handleEmailLogin}>
                                        <div className="form-box" style={{ marginBottom: '20px' }}>
                                            <input
                                                type="email"
                                                name="email"
                                                className="form-input"
                                                placeholder={t('loginPage.emailAddress')}
                                                required
                                                style={{ width: '100%' }}
                                            />
                                        </div>
                                        <div className="form-box" style={{ marginBottom: '8px' }}>
                                            <input
                                                type="password"
                                                name="password"
                                                className="form-input"
                                                placeholder={t('loginPage.password')}
                                                required
                                                style={{ width: '100%' }}
                                            />
                                        </div>
                                        <div style={{ textAlign: 'right', marginBottom: '20px' }}>
                                            <Link to="/forgot-password" style={{ color: '#d89d37', fontSize: '13px', textDecoration: 'none' }}>
                                                {t('loginPage.forgotPassword')}
                                            </Link>
                                        </div>
                                        <button
                                            type="submit"
                                            className="sec-btn"
                                            disabled={isLoading}
                                            style={{
                                                width: '100%',
                                                padding: '15px',
                                                marginBottom: '15px',
                                                opacity: isLoading ? 0.7 : 1,
                                                cursor: isLoading ? 'not-allowed' : 'pointer'
                                            }}
                                        >
                                            <span>{isLoading ? t('loginPage.signingIn') : t('loginPage.signIn')}</span>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => { setShowEmailLogin(false); setLoginError(''); }}
                                            style={{
                                                width: '100%',
                                                padding: '15px',
                                                border: '1px solid #c8d8dd',
                                                backgroundColor: '#ffffff',
                                                borderRadius: '4px',
                                                fontSize: '16px',
                                                cursor: 'pointer',
                                                color: '#2c3d4f'
                                            }}
                                        >
                                            Back to OAuth Options
                                        </button>
                                    </form>
                                </div>
                            )}

                            <div className="text-center mt-4">
                                <p style={{ color: '#666', fontSize: '14px' }}>
                                    Don't have an account? <a href="/register" style={{ color: '#d89d37', textDecoration: 'none' }}>Sign Up</a>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Login;